import { findScrollableContainerWithRoom, getPrimaryDirection } from './scroll-detection';
import { type WheelEventHandler } from './wheel-event-handler';

/**
 * Detects when native scrolling occurs and signals when the scroll gesture completes.
 * This handler does not perform scrolling itself - it allows the browser's native
 * scrolling behavior to occur naturally, and listens for the scrollend event to
 * notify the system when the scroll gesture is complete.
 */
export class ScrollEndHandler implements WheelEventHandler {
  private scrollableContainer: Element | null = null;
  private scrollEndListener: (() => void) | null = null;

  constructor(private onGestureEnd: () => void) {}

  public canHandle(event: WheelEvent): boolean {
    // Only handle event when it occurs on a scrollable container in event direction
    return this.findScrollableContainer(event) !== null;
  }

  public start(event: WheelEvent): void {
    this.scrollableContainer = this.findScrollableContainer(event);

    if (!this.scrollableContainer) {
      return;
    }

    const handleScrollEnd = () => {
      this.onGestureEnd();
    };

    this.scrollableContainer.addEventListener('scrollend', handleScrollEnd, {
      once: true,
    });

    this.scrollEndListener = () => {
      this.scrollableContainer?.removeEventListener('scrollend', handleScrollEnd);
    };
  }

  public handleEvent(_event: WheelEvent): void {
    // Intentionally empty - allows native browser scrolling by not preventing default behavior
  }

  public cleanup(): void {
    if (this.scrollEndListener) {
      this.scrollEndListener();
      this.scrollEndListener = null;
    }
    this.scrollableContainer = null;
  }

  /**
   * Traverses DOM upwards to find any element that has scroll room in the
   * primary direction of the wheel event
   */
  private findScrollableContainer(event: WheelEvent): Element | null {
    const target = event.target as Element;
    const direction = getPrimaryDirection(event);
    return findScrollableContainerWithRoom(target, document, direction, event);
  }
}
